import React from "react";
import { useBrand } from "@/context/BrandContext";

/*
  HerbKranti — SVG BrandMark
  A faithful vector reconstruction of the HK monogram:
    - Orange "H" (two thick vertical strokes with rounded ends)
    - Orange "K" sharing the right vertical of the H
    - Gold ribbon curving across as the connective horizontal
    - Botanical leaf tip integrated into the upper-right of the K
  Colours draw from brand tokens (--hk-orange / --hk-gold).
  Rendered at any size; the viewBox is 0 0 120 120 for a compact roundel.
*/

const HK_MONOGRAM_ID = "hk-monogram-svg";

export const HKMonogram = ({ size = 40, className = "", withRoundel = true, title = "HerbKranti" }) => {
  const dim = typeof size === "number" ? `${size}px` : size;
  // If Admin › Logo Management has a square icon/monogram, use it as the mark.
  // Otherwise fall back to the vector monogram below.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { brand } = useBrand();
  const logoUrl = brand?.logo_mark;
  if (logoUrl) {
    return (
      <span
        className={"inline-block align-middle select-none " + className}
        style={{ width: dim, height: dim }}
        aria-label={title}
        role="img"
      >
        <img src={logoUrl} alt={title} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      </span>
    );
  }
  return (
    <span
      className={"inline-block align-middle select-none " + className}
      style={{ width: dim, height: dim }}
      aria-hidden="false"
      role="img"
      aria-label={title}
    >
      <svg
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id={`${HK_MONOGRAM_ID}-orange`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#E48145" />
            <stop offset="100%" stopColor="#B85E24" />
          </linearGradient>
          <linearGradient id={`${HK_MONOGRAM_ID}-gold`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D9BE7E" />
            <stop offset="100%" stopColor="#A8894A" />
          </linearGradient>
          <linearGradient id={`${HK_MONOGRAM_ID}-ivory`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FAF6EB" />
            <stop offset="100%" stopColor="#F0E7D2" />
          </linearGradient>
        </defs>

        {/* Optional roundel (ivory disc with fine gold hairline) */}
        {withRoundel && (
          <>
            <circle cx="60" cy="60" r="58" fill={`url(#${HK_MONOGRAM_ID}-ivory)`} />
            <circle cx="60" cy="60" r="58" fill="none" stroke="rgba(168, 137, 74, 0.32)" strokeWidth="0.8" />
          </>
        )}

        {/* H — left vertical stroke */}
        <rect x="26" y="26" width="10" height="68" rx="3" fill={`url(#${HK_MONOGRAM_ID}-orange)`} />
        {/* H right / K left — shared vertical stroke */}
        <rect x="56" y="26" width="10" height="68" rx="3" fill={`url(#${HK_MONOGRAM_ID}-orange)`} />

        {/* Gold ribbon — H's horizontal, elegantly curved (connects the two verticals) */}
        <path
          d="M 32 62
             C 40 52, 52 72, 61 62"
          fill="none"
          stroke={`url(#${HK_MONOGRAM_ID}-gold)`}
          strokeWidth="6.5"
          strokeLinecap="round"
        />

        {/* K — upper diagonal (stops before the leaf) */}
        <path
          d="M 61 60 L 82 40"
          stroke={`url(#${HK_MONOGRAM_ID}-orange)`}
          strokeWidth="9"
          strokeLinecap="round"
          fill="none"
        />
        {/* K — lower diagonal */}
        <path
          d="M 61 60 L 90 94"
          stroke={`url(#${HK_MONOGRAM_ID}-orange)`}
          strokeWidth="9"
          strokeLinecap="round"
          fill="none"
        />

        {/* Botanical leaf tip on the top-right of K */}
        <path
          d="M 82 40
             C 88 32, 96 30, 100 22
             C 96 30, 94 38, 88 44
             Z"
          fill={`url(#${HK_MONOGRAM_ID}-orange)`}
        />
        {/* Leaf veinlet — a delicate gold hairline for premium detail */}
        <path
          d="M 84 40 C 90 34, 94 30, 98 24"
          stroke="rgba(168, 137, 74, 0.7)"
          strokeWidth="0.7"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Wordmark — "HerbKranti" as clean Playfair-based typography          */
/* ------------------------------------------------------------------ */
export const HKWordmark = ({
  size = 22,
  color = "var(--hk-ink)",
  className = "",
  showTagline = false,
  taglineColor = "var(--hk-gold-deep)",
  taglineSize = 9,
  hideTaglineOnMobile = false,
}) => (
  <span className={"inline-flex flex-col leading-none " + className}>
    <span
      className="serif font-medium"
      style={{
        fontSize: `${size}px`,
        letterSpacing: "-0.005em",
        color,
        lineHeight: 1.05,
        paddingBottom: "0.06em", // safeguards descenders (though HerbKranti has none)
      }}
    >
      HerbKranti
    </span>
    {showTagline && (
      <span
        className={"mt-[6px] " + (hideTaglineOnMobile ? "hidden lg:inline-flex" : "")}
        style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: `${taglineSize}px`,
          letterSpacing: "0.32em",
          color: taglineColor,
          fontWeight: 500,
          textTransform: "uppercase",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
        }}
      >
        Rediscover Nature · Redefine Wellness
      </span>
    )}
  </span>
);

/* ------------------------------------------------------------------ */
/* Combined lockup: monogram + wordmark + optional tagline             */
/* ------------------------------------------------------------------ */
export const BrandLockup = ({
  markSize = 40,
  wordSize = 22,
  showTagline = true,
  color = "var(--hk-ink)",
  taglineColor = "var(--hk-gold-deep)",
  taglineSize = 9,
  withRoundel = true,
  className = "",
  testId,
}) => (
  <span
    className={"inline-flex items-center gap-3 " + className}
    data-testid={testId}
  >
    <HKMonogram size={markSize} withRoundel={withRoundel} />
    <HKWordmark
      size={wordSize}
      color={color}
      showTagline={showTagline}
      taglineColor={taglineColor}
      taglineSize={taglineSize}
    />
  </span>
);

/* ------------------------------------------------------------------ */
/* BrandLogo — THE brand presence everywhere (header, footer, admin…)  */
/* Shows the uploaded image logo from Admin › Logo Management when one */
/* exists; otherwise falls back to monogram + "HerbKranti" wordmark.   */
/* ------------------------------------------------------------------ */
export const BrandLogo = ({
  variant = "light",          // "light" = on light bg · "dark" = on dark bg (uses logo_dark if uploaded)
  height,                     // px — defaults to admin-set logo_height (44)
  markSize = 40,
  wordSize = 20,
  showTagline = true,
  taglineSize = 9,
  hideTaglineOnMobile = false,
  className = "",
  testId = "brand-logo",
}) => {
  const { brand } = useBrand();
  const src = variant === "dark" ? (brand?.logo_dark || brand?.logo) : brand?.logo;
  const h = Number(height || brand?.logo_height) || 44;
  if (src) {
    return (
      <span className={"inline-flex items-center select-none " + className} data-testid={testId} data-logo-mode="image">
        <img
          src={src}
          alt={brand?.brand_name || "HerbKranti"}
          style={{ height: `${h}px`, maxWidth: `${Math.round(h * 5)}px` }}
          className="w-auto object-contain object-left"
        />
      </span>
    );
  }
  const dark = variant === "dark";
  return (
    <span className={"inline-flex items-center gap-3 " + className} data-testid={testId} data-logo-mode="fallback">
      <HKMonogram size={markSize} withRoundel={true} />
      <HKWordmark
        size={wordSize}
        color={dark ? "#FFFFFF" : "var(--hk-ink)"}
        showTagline={showTagline}
        taglineColor={dark ? "var(--hk-gold)" : "var(--hk-gold-deep)"}
        taglineSize={taglineSize}
        hideTaglineOnMobile={hideTaglineOnMobile}
      />
    </span>
  );
};

export default BrandLockup;
